import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Register.css';

const API_BASE = 'https://digitalapi.definiteassurance.com/api';
const COMPANY = 'Definite';

const Register = () => {
  const navigate = useNavigate();

  // ── Form state ──
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [identification, setIdentification] = useState('');
  const [krapin, setKrapin] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeIntermediary, setAgreeIntermediary] = useState(false);
  const [buttonText, setButtonText] = useState('Register Now');

  // ── Data state ──
  const [regions, setRegions] = useState([]);
  const [credentials, setCredentials] = useState(null);

  // ── Fetch API credentials on mount ──
  useEffect(() => {
    const getCredentials = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/getCredentials?username=${COMPANY}`
        );
        const { Username, Password } = await res.json();
        setCredentials(btoa(`${Username}:${Password}`));
      } catch (err) {
        console.error('Failed to fetch credentials', err);
      }
    };
    getCredentials();
  }, []);

  // ── Fetch regions on mount ──
  useEffect(() => {
    const getRegions = async () => {
      try {
        const res = await fetch(`${API_BASE}/getRegions`);
        const data = await res.json();
        setRegions(data);
      } catch (err) {
        console.error('Failed to fetch regions', err);
      }
    };
    getRegions();
  }, []);

  // ── Register handler ──
  const handleRegister = async (e) => {
    e.preventDefault();

    if (!name || !email || !phone || !identification || !krapin || !password || !confirmPassword) {
      toast.error('All fields marked * are required!');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!agreeTerms || !agreeIntermediary) {
      toast.error('You must agree to both the Privacy Policy & Terms of Use and the Intermediary Agreement');
      return;
    }

    try {
      setButtonText('Please wait...');
      const res = await fetch(`${API_BASE}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone,
          identification,
          krapin,
          password,
          selectedRegion,
        }),
        credentials: 'include',
      });

      const { success, exists } = await res.json();

      if (success && !exists) {
        toast.success('Account successfully registered');
        navigate('/login');
      } else if (!success && exists) {
        toast.error('Account already exists');
      } else {
        toast.error('Something went wrong! Please try again later');
      }
    } catch (err) {
      toast.error('Network error — please try again');
      console.error(err);
    } finally {
      setButtonText('Register Now');
    }
  };

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="register-page">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="register-content">
        {/* ── Left image panel ── */}
        <div className="register-image-panel">
          <img src="/insurancebanner.jpg" alt="Insurance banner" />
        </div>

        {/* ── Right form panel ── */}
        <div className="register-form-panel">
          <div className="register-card">
            {/* ── Pink header ── */}
            <div className="register-header">
              <div className="register-header-text">
                <img
                  src="/definitelogo.png"
                  alt="Definite Assurance"
                  className="register-logo"
                />
                <p className="subtitle">Get a free account now.</p>
              </div>
              <img
                src="/support.png"
                alt="Illustration"
                className="register-header-illustration"
              />
            </div>

            {/* ── Form ── */}
            <div className="register-body">
              <form onSubmit={handleRegister}>
                {/* Row 1: Name & Email */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name*</label>
                    <input
                      id="name"
                      type="text"
                      placeholder="Enter your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email*</label>
                    <input
                      id="email"
                      type="email"
                      placeholder="Enter email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                {/* Row 2: Phone & KRA PIN */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone number*</label>
                    <input
                      id="phone"
                      type="tel"
                      placeholder="0712345678"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="krapin">KRA PIN*</label>
                    <input
                      id="krapin"
                      type="text"
                      placeholder="KRA PIN"
                      value={krapin}
                      onChange={(e) => setKrapin(e.target.value)}
                    />
                  </div>
                </div>

                {/* Row 3: IRA License & Branch */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="identification">IRA License Number*</label>
                    <input
                      id="identification"
                      type="text"
                      placeholder="IRA License Number"
                      value={identification}
                      onChange={(e) => setIdentification(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="region">SELECT BRANCH</label>
                    <select
                      id="region"
                      value={selectedRegion}
                      onChange={(e) => setSelectedRegion(e.target.value)}
                    >
                       <option disabled selected>SELECT BRANCH</option>
                          <option value='437'>Bungoma</option>
                           <option value="639">Development House</option>
                          <option value="641">Eldoret</option>
                          <option value="588">Embu</option>
                           <option value="630">Head Office</option>
                           <option value="447">Kiambu</option>
                          <option value="593">Kakamega</option>
                          <option value="626">Kapsabet</option>
                          <option value="594">Kericho</option>
                          <option value="598">Kisii</option>
                          <option value="599">Kisumu</option>
                          <option value="640">Kitale</option>
                          <option value="604">Machakos</option>
                          <option value="642">Malindi</option>
                          <option value="608">Meru</option>
                          <option value="609">Migori</option>
                          <option value="610">Mombasa</option>
                          <option value="611">Murang'a</option>
                          <option value="615">Narok</option>
                          <option value="644">Nyahururu</option>
                          <option value="618">Nyeri</option>
                          <option value="613">Nakuru</option>
                          <option value="645">Naivasha</option>
                          <option value="638">Thika</option>
                    </select>
                  </div>
                </div>

                {/* Row 4: Password & Confirm Password */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="password">Password*</label>
                    <input
                      id="password"
                      type="password"
                      placeholder="Enter Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password*</label>
                    <input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>
                </div>

                {/* Agreement checkboxes */}
                <div className="form-agreements">
                  <label className="agreement-checkbox">
                    <input
                      type="checkbox"
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                    />
                    <span>
                      I agree to the{' '}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer">
                        Privacy Policy and Terms of Use
                      </Link>
                    </span>
                  </label>
                  <label className="agreement-checkbox">
                    <input
                      type="checkbox"
                      checked={agreeIntermediary}
                      onChange={(e) => setAgreeIntermediary(e.target.checked)}
                    />
                    <span>
                      I agree to the{' '}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer">
                        Intermediary Agreement
                      </Link>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  className="register-btn"
                  disabled={buttonText !== 'Register Now' || !agreeTerms || !agreeIntermediary}
                >
                  {buttonText}
                </button>
              </form>

              <div className="register-footer-text">
                <p>
                  Already have an account ? <Link to="/login">Login</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Page footer ── */}
      <div className="register-page-footer">
        © 2026 Definite Agents Portal. Crafted by victrie.com
      </div>
    </div>
  );
};

export default Register;
